import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { translate } from '../../../core'
import { PurchaseOrder, PurchaseOrderRequest } from '../type/purchase-order'
import { purchaseOrderStatusLabelMapper } from '../func/purchaseOrderStatusMapper'

export const PURCHASE_ORDER_LIST_PATH = '/purchase-order'
export const PURCHASE_ORDER_PAGE_SIZE_OPTIONS = [10, 20, 50, 100]
export const PURCHASE_ORDER_DEFAULT_PAGE_SIZE = 10

const LIST_LOCATION_KEY = 'purchase-order.list-location'

const VENDOR_LABEL_PARAM = 'vendorName'

export type PurchaseOrderSort = {
  field: string
  order: 'ascend' | 'descend'
}

export type PurchaseOrderTableState = {
  page: number
  pageSize: number
  sort?: PurchaseOrderSort
}

export type PurchaseOrderFilterDescriptor = {
  key: string
  requestKey?: keyof PurchaseOrderRequest
  label: string
  parse: (raw: string) => unknown
  labelParam?: string
  format?: (raw: string) => string
}

export const PURCHASE_ORDER_FILTERS: PurchaseOrderFilterDescriptor[] = [
  {
    key: 'number',
    requestKey: 'number',
    label: translate('purchase_order_nu_abbr'),
    parse: raw => (raw.trim() !== '' && Number.isInteger(Number(raw)) ? Number(raw) : undefined),
    format: raw => `#${raw}`,
  },
  {
    key: 'status',
    requestKey: 'status',
    label: translate('status'),
    parse: raw => (raw in purchaseOrderStatusLabelMapper ? raw : undefined),
    format: raw => purchaseOrderStatusLabelMapper[raw as PurchaseOrder['status']] ?? raw,
  },
  {
    key: 'vendorId',
    requestKey: 'vendorId',
    label: translate('vendor'),
    parse: raw => (raw.trim() !== '' && Number.isInteger(Number(raw)) ? Number(raw) : undefined),
    labelParam: VENDOR_LABEL_PARAM,
  },
]

export type PurchaseOrderFilterTag = {
  key: string
  label: string
  value: string
}

const isSortOrder = (value: string | null): value is PurchaseOrderSort['order'] =>
  value === 'ascend' || value === 'descend'

const setOrDelete = (params: URLSearchParams, key: string, value?: string | null) => {
  if (value == null || value === '')
    params.delete(key)
  else
    params.set(key, value)
}

export const usePurchaseOrderListState = () => {
  const [params, setParams] = useSearchParams()

  const table = useMemo<PurchaseOrderTableState>(() => {
    const page = Number(params.get('page'))
    const pageSize = Number(params.get('pageSize'))
    const sortField = params.get('sortField')
    const sortOrder = params.get('sortOrder')

    return {
      page: Number.isInteger(page) && page > 0 ? page : 1,
      pageSize: PURCHASE_ORDER_PAGE_SIZE_OPTIONS.includes(pageSize)
        ? pageSize
        : PURCHASE_ORDER_DEFAULT_PAGE_SIZE,
      sort: sortField && isSortOrder(sortOrder)
        ? {field: sortField, order: sortOrder}
        : undefined,
    }
  }, [params])

  const filters = useMemo<PurchaseOrderRequest>(() => {
    const result: Record<string, unknown> = {}

    PURCHASE_ORDER_FILTERS.forEach(filter => {
      if (!filter.requestKey) return

      const raw = params.get(filter.key)
      if (raw == null) return

      const value = filter.parse(raw)
      if (value === undefined) return

      result[filter.requestKey] = value
    })

    return result as PurchaseOrderRequest
  }, [params])

  const request = useMemo(() => ({
    take: table.pageSize,
    skip: table.pageSize * (table.page - 1),
    ...filters,
  }), [table, filters])

  const filterTags = useMemo<PurchaseOrderFilterTag[]>(() =>
    PURCHASE_ORDER_FILTERS.flatMap(filter => {
      const raw = params.get(filter.key)
      if (raw == null || filter.parse(raw) === undefined) return []

      const value = (filter.labelParam ? params.get(filter.labelParam) : null)
        ?? filter.format?.(raw)
        ?? raw

      return [{key: filter.key, label: filter.label, value}]
    }), [params])

  const filterParams = useMemo(() => {
    const result: Record<string, string> = {}

    PURCHASE_ORDER_FILTERS.forEach(filter => {
      [filter.key, filter.labelParam].forEach(key => {
        const raw = key ? params.get(key) : null
        if (key && raw != null) result[key] = raw
      })
    })

    return result
  }, [params])

  const write = useCallback((mutate: (next: URLSearchParams) => void) => {
    setParams(prev => {
      const next = new URLSearchParams(prev)
      mutate(next)
      return next
    }, {replace: true})
  }, [setParams])

  const setTableState = useCallback((next: PurchaseOrderTableState) => {
    write(params => {
      setOrDelete(params, 'page', next.page > 1 ? String(next.page) : null)
      setOrDelete(
        params,
        'pageSize',
        next.pageSize !== PURCHASE_ORDER_DEFAULT_PAGE_SIZE ? String(next.pageSize) : null
      )
      setOrDelete(params, 'sortField', next.sort?.field)
      setOrDelete(params, 'sortOrder', next.sort?.order)
    })
  }, [write])

  const dropFilters = (params: URLSearchParams) =>
    PURCHASE_ORDER_FILTERS.forEach(filter => {
      params.delete(filter.key)
      if (filter.labelParam) params.delete(filter.labelParam)
    })

  /**
   * @param request filter values keyed by their `purchaseOrdersFind` field
   * @param labels human readable values keyed by filter key (vendor name, ...)
   */
  const applyFilters = useCallback((
    request: Record<string, unknown>,
    labels?: Record<string, string>
  ) => {
    write(params => {
      dropFilters(params)

      PURCHASE_ORDER_FILTERS.forEach(filter => {
        if (!filter.requestKey) return

        const value = request[filter.requestKey]
        if (value == null || value === '') return

        params.set(filter.key, String(value))

        const label = labels?.[filter.key]
        if (filter.labelParam && label) params.set(filter.labelParam, label)
      })

      params.delete('page')
    })
  }, [write])

  const clearFilters = useCallback(() => {
    write(params => {
      dropFilters(params)
      params.delete('page')
    })
  }, [write])

  const removeFilter = useCallback((key: string) => {
    write(params => {
      const filter = PURCHASE_ORDER_FILTERS.find(it => it.key === key)
      params.delete(key)
      if (filter?.labelParam) params.delete(filter.labelParam)
      params.delete('page')
    })
  }, [write])

  return {
    table,
    request,
    filterTags,
    filterParams,
    hasFilter: filterTags.length > 0,
    setTableState,
    applyFilters,
    clearFilters,
    removeFilter,
  }
}

export const rememberPurchaseOrderListLocation = (search: string) => {
  try {
    sessionStorage.setItem(LIST_LOCATION_KEY, search)
  } catch {
    // private mode / disabled storage: the browser back button still restores the url
  }
}

export const readPurchaseOrderListLocation = () => {
  try {
    const search = sessionStorage.getItem(LIST_LOCATION_KEY)
    return search && search.startsWith('?')
      ? `${PURCHASE_ORDER_LIST_PATH}${search}`
      : PURCHASE_ORDER_LIST_PATH
  } catch {
    return PURCHASE_ORDER_LIST_PATH
  }
}
